import {
  Document,
  Footer,
  Header,
  Packer,
  PageNumber,
  PageOrientation,
  Paragraph,
  Table,
  AlignmentType,
  WidthType,
  TableRow,
  TableCell,
  BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';
import { SafeTextRun } from '@utils/docx';
import type { SurveyInitiative, UnitConfig } from '../../types/survey';
import type { ReportData } from '../../types/reportData';
import type { ISSBAIContent, DownloadMultiScope } from '../../types/download';
import { getTypeTags } from './group-builder';
import { generateFilename } from './outputs';
import { getTableData, renderQuestionValue } from './questionView';
import { heading, paragraph, arrayToTableRow, cellToTableCell, spacer, type Styles, type ArrayToTableCellType } from './document-structure';
import { UtrValueType } from '@g17eco/core';
import { UtrvStatus } from '@constants/status';
import { declarativeStyles } from './styles';

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };

const HEADING_COLOR = '1F3864';
const ISSB_COLOR = '2E74B5';
const TEMPLATE_BLUE_FILL = 'D9E1F2';
const TEMPLATE_GREEN_FILL = 'E2EFDA';
const TEMPLATE_GREEN_TEXT = '385723';

const ISSB_HEADING_STYLE: Styles = {
  textRun: { bold: true, size: 30, color: HEADING_COLOR },
  spacing: { before: 400, after: 200 },
};

const ISSB_BOLD_STYLE: Styles = {
  textRun: { bold: true, size: 24 },
  spacing: { before: 200, after: 100 },
};

const ISSB_TEXT_STYLE: Styles = {
  textRun: { size: 22 },
  spacing: { after: 100 },
};

const ISSB_CELL_STYLE: Styles = {
  textRun: { size: 20, color: '000000' },
  spacing: { before: 0, after: 0 },
};

const ISSB_HEADER_CELL_STYLE: Styles = {
  textRun: { bold: true, size: 20, color: 'FFFFFF' },
  shading: { fill: ISSB_COLOR },
  spacing: { before: 0, after: 0 },
};

const HEADER_BOTTOM_BORDER = {
  top: NO_BORDER,
  bottom: { style: BorderStyle.SINGLE, size: 12, color: ISSB_COLOR },
  left: NO_BORDER,
  right: NO_BORDER,
};

const EXEC_SUMMARY_BLUE_HEADER: Styles = {
  textRun: { bold: true, size: 20, color: HEADING_COLOR },
  shading: { fill: TEMPLATE_BLUE_FILL },
  spacing: { before: 0, after: 0 },
  alignment: AlignmentType.CENTER,
};

const EXEC_SUMMARY_GREEN_HEADER: Styles = {
  textRun: { bold: true, size: 20, color: TEMPLATE_GREEN_TEXT },
  shading: { fill: TEMPLATE_GREEN_FILL },
  spacing: { before: 0, after: 0 },
  alignment: AlignmentType.CENTER,
};

const EXEC_SUMMARY_VALUE_BLUE: Styles = {
  textRun: { bold: true, size: 40, color: HEADING_COLOR },
  spacing: { before: 0, after: 0 },
  alignment: AlignmentType.CENTER,
};

const EXEC_SUMMARY_VALUE_GREEN: Styles = {
  textRun: { bold: true, size: 40, color: TEMPLATE_GREEN_TEXT },
  spacing: { before: 0, after: 0 },
  alignment: AlignmentType.CENTER,
};

interface PillarStat {
  standard: string;
  pillar: string;
  total: number;
  verified: number;
  notSubmitted: number;
  completion: number;
}

const S1_PILLARS: Array<[string, string]> = [
  ['issb_governance_s1', 'Governance'],
  ['issb_strategy_s1', 'Strategy'],
  ['issb_strategy_s1_res', 'Resilience'],
  ['issb_risk_s1', 'Risk Management'],
  ['issb_metntar_s1', 'Metrics and Targets'],
  ['issb_genreq_s1', 'General Requirements'],
];

const S2_PILLARS: Array<[string, string]> = [
  ['issb_governance_s2', 'Governance'],
  ['issb_strategy_s2', 'Strategy'],
  ['issb_strategy_s2_cr', 'Climate resilience'],
  ['issb_risk_s2', 'Risk Management'],
  ['issb_metntar_s2', 'Metrics and Targets'],
];

const computePillarStats = (metrics: ReportData[], tagsMap: Map<ReportData, string[]>): PillarStat[] => {
  const stats: PillarStat[] = [];
  const pillarGroups: Array<{ standard: string; pillars: Array<[string, string]> }> = [
    { standard: 'IFRS S1', pillars: S1_PILLARS },
    { standard: 'IFRS S2', pillars: S2_PILLARS },
  ];

  for (const { standard, pillars } of pillarGroups) {
    for (const [tagCode, pillarName] of pillars) {
      const matching = metrics.filter((m) => tagsMap.get(m)?.includes(tagCode));
      if (matching.length > 0) {
        const verified = matching.filter((m) => m.status === UtrvStatus.Verified).length;
        stats.push({
          standard,
          pillar: pillarName,
          total: matching.length,
          verified,
          notSubmitted: matching.length - verified,
          completion: verified / matching.length,
        });
      }
    }
  }
  return stats;
};

const groupByPillar = (
  metrics: ReportData[],
  pillars: Array<[string, string]>,
  tagsMap: Map<ReportData, string[]>,
) =>
  pillars.reduce<Array<{ pillar: string; metrics: ReportData[] }>>((groups, [tagCode, pillarName]) => {
    const pillarMetrics = metrics.filter((m) => tagsMap.get(m)?.includes(tagCode));
    if (pillarMetrics.length > 0) {
      groups.push({ pillar: pillarName, metrics: pillarMetrics });
    }
    return groups;
  }, []);

interface AppendixEntry {
  heading: string;
  tableData: string[][];
}

const buildAppendixTable = (data: string[][]): Table =>
  new Table({
    rows: data.map((row, i) =>
      arrayToTableRow(row, i === 0 ? ISSB_HEADER_CELL_STYLE : ISSB_CELL_STYLE),
    ),
    width: { size: 100, type: WidthType.PERCENTAGE },
  });

const createAppendixCollector = () => {
  const entries: AppendixEntry[] = [];
  const existingNames = new Map<string, string>();

  return {
    add(name: string, tableData: string[][]): string {
      const existing = existingNames.get(name);
      if (existing) {
        return existing;
      }

      const headingText = `Appendix ${entries.length + 1}: ${name}`;
      entries.push({ heading: headingText, tableData });
      existingNames.set(name, headingText);
      return headingText;
    },
    hasEntries(): boolean {
      return entries.length > 0;
    },
    render(): (Paragraph | Table)[] {
      return [
        heading('Appendix', ISSB_HEADING_STYLE),
        ...entries.flatMap((entry) => [
          paragraph(entry.heading, ISSB_BOLD_STYLE),
          buildAppendixTable(entry.tableData),
        ]),
      ];
    },
  };
};

const buildTitleBlock = (companyName: string, reportingPeriod: string): Paragraph[] => [
  paragraph(companyName, {
    textRun: { bold: true, size: 48, color: HEADING_COLOR },
    alignment: AlignmentType.CENTER,
    spacing: { after: 100 },
  }),
  paragraph('ISSB Metrics Summary Report', {
    textRun: { bold: true, size: 28, color: ISSB_COLOR },
    alignment: AlignmentType.CENTER,
    spacing: { after: 100 },
  }),
  paragraph(`Annual Reporting Period | ${reportingPeriod}`, {
    textRun: { size: 24, color: '595959' },
    alignment: AlignmentType.CENTER,
    spacing: { after: 200 },
  }),
  paragraph(
    'IFRS S1 General Requirements for Disclosure of Sustainability-related Financial Information',
    { textRun: { size: 20, color: '595959', italics: true }, alignment: AlignmentType.CENTER, spacing: { after: 100 } }
  ),
  paragraph(
    'IFRS S2 Climate-related Disclosures',
    { textRun: { size: 20, color: '595959', italics: true }, alignment: AlignmentType.CENTER, spacing: { after: 400 } }
  ),
];

const buildSummaryStatsTable = (s1Count: number, s2Count: number, totalCount: number, verifiedCount: number): Table =>
  new Table({
    rows: [
      new TableRow({
        children: [
          cellToTableCell('Total metrics', EXEC_SUMMARY_BLUE_HEADER),
          cellToTableCell('IFRS S1', EXEC_SUMMARY_BLUE_HEADER),
          cellToTableCell('IFRS S2', EXEC_SUMMARY_BLUE_HEADER),
          cellToTableCell('Verified', EXEC_SUMMARY_GREEN_HEADER),
        ]
      }),
      new TableRow({
        children: [
          cellToTableCell(String(totalCount), EXEC_SUMMARY_VALUE_BLUE),
          cellToTableCell(String(s1Count), EXEC_SUMMARY_VALUE_BLUE),
          cellToTableCell(String(s2Count), EXEC_SUMMARY_VALUE_BLUE),
          cellToTableCell(`${verifiedCount} / ${totalCount}`, EXEC_SUMMARY_VALUE_GREEN),
        ]
      }),
    ],
    width: { size: 100, type: WidthType.PERCENTAGE },
  });

const buildPillarTable = (stats: PillarStat[]): Table =>
  new Table({
    rows: [
      arrayToTableRow(
        ['Standard', 'Pillar', 'Total', 'Verified', 'Not submitted', 'Completion'],
        ISSB_HEADER_CELL_STYLE,
      ),
      ...stats.map((stat) =>
        arrayToTableRow(
          [
            stat.standard,
            stat.pillar,
            String(stat.total),
            stat.verified,
            String(stat.notSubmitted),
            `${Math.round(stat.completion * 100)}%`,
          ],
          ISSB_CELL_STYLE,
        ),
      ),
    ],
    width: { size: 100, type: WidthType.PERCENTAGE },
  });

const buildMetricDetailTable = (
  metrics: ReportData[],
  appendix: ReturnType<typeof createAppendixCollector>,
  unitConfig: UnitConfig | undefined,
  displayUserInput: boolean,
): Table =>
  new Table({
    rows: [
      arrayToTableRow(
        ['Code', 'Disclosure requirement', 'Status', 'Response / value'],
        ISSB_HEADER_CELL_STYLE,
      ),
      ...metrics.map((m) => {
        const isVerified = m.status === UtrvStatus.Verified;
        const valueType = m.valueType ?? m.universalTracker.valueType;

        let responseCell: ArrayToTableCellType = '';
        if (valueType === UtrValueType.Table) {
          const tableData = getTableData(m, unitConfig, displayUserInput);
          if (tableData && tableData.length > 1) {
            responseCell = appendix.add(m.universalTracker.name, tableData);
          }
        } else if (isVerified) {
          responseCell = renderQuestionValue(m, unitConfig, ISSB_CELL_STYLE, { displayUserInput }) ?? '';
        }

        return arrayToTableRow(
          [
            m.universalTracker.code,
            m.universalTracker.name,
            isVerified ? 'Verified' : 'Not submitted',
            responseCell,
          ],
          ISSB_CELL_STYLE,
        );
      }),
    ],
    width: { size: 100, type: WidthType.PERCENTAGE },
  });

const buildNextStepsList = (nextSteps: ISSBAIContent['nextSteps']): Paragraph[] =>
  nextSteps.flatMap((step, index) => [
    paragraph(`${index + 1}. ${step.title}`, {
      textRun: { bold: true, size: 22 },
      spacing: { before: 200 },
    }),
    paragraph(step.explanation, ISSB_TEXT_STYLE),
  ]);

const buildDefaultHeader = (companyName: string) =>
  new Header({
    children: [
      new Table({
        rows: [
          new TableRow({
            children: [
              new TableCell({
                children: [paragraph(companyName, { textRun: { size: 18, color: '999999' }, alignment: AlignmentType.LEFT, spacing: { before: 0, after: 100 } })],
                borders: HEADER_BOTTOM_BORDER,
                width: { size: 33, type: WidthType.PERCENTAGE },
              }),
              new TableCell({
                children: [paragraph('ISSB Metrics Summary Report', { textRun: { size: 18, color: '999999' }, alignment: AlignmentType.CENTER, spacing: { before: 0, after: 100 } })],
                borders: HEADER_BOTTOM_BORDER,
                width: { size: 34, type: WidthType.PERCENTAGE },
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new SafeTextRun({ text: 'Powered by ', size: 18, color: '999999' }),
                      new SafeTextRun({ text: 'G17Eco', bold: true, size: 18, color: '999999' }),
                    ],
                    alignment: AlignmentType.RIGHT,
                    spacing: { before: 0, after: 100 },
                  }),
                ],
                borders: HEADER_BOTTOM_BORDER,
                width: { size: 33, type: WidthType.PERCENTAGE },
              }),
            ],
          }),
        ],
        width: { size: 100, type: WidthType.PERCENTAGE },
      }),
    ],
  });

const buildDefaultFooter = () =>
  new Footer({
    children: [
      new Paragraph({
        children: [
          new SafeTextRun({ text: 'Confidential | Page ', size: 18, color: '999999' }),
          new SafeTextRun({ children: [PageNumber.CURRENT], size: 18, color: '999999' }),
          new SafeTextRun({ text: ' of ', size: 18, color: '999999' }),
          new SafeTextRun({ children: [PageNumber.TOTAL_PAGES], size: 18, color: '999999' }),
        ],
        alignment: AlignmentType.CENTER,
      }),
    ],
  });

export const generateISSBEnhancedReport = async (params: {
  survey: SurveyInitiative;
  reportData: ReportData[];
  aiContent: ISSBAIContent;
  downloadScope: DownloadMultiScope;
}): Promise<void> => {
  const { survey, reportData, aiContent } = params;
  const { displayUserInput = false } = params.downloadScope;
  const appendix = createAppendixCollector();

  const companyName = survey.initiative?.name ?? 'Unknown Company';
  const reportingPeriod = survey.effectiveDate
    ? new Date(survey.effectiveDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'N/A';

  const tagsMap = new Map(reportData.map((m) => [m, getTypeTags(m.universalTracker)]));

  const s1Data = reportData.filter((m) => tagsMap.get(m)?.includes('ifrs_s1'));
  const s2Data = reportData.filter((m) => tagsMap.get(m)?.includes('ifrs_s2'));
  const totalCount = reportData.length;
  const verifiedCount = reportData.filter((m) => m.status === UtrvStatus.Verified).length;
  const pillarStats = computePillarStats(reportData, tagsMap);
  const s1Groups = groupByPillar(s1Data, S1_PILLARS, tagsMap);
  const s2Groups = groupByPillar(s2Data, S2_PILLARS, tagsMap);

  const doc = new Document({
    styles: { paragraphStyles: declarativeStyles },
    sections: [
      {
        headers: { default: buildDefaultHeader(companyName) },
        footers: { default: buildDefaultFooter() },
        children: [
          ...buildTitleBlock(companyName, reportingPeriod),
          heading('1. Executive Summary', ISSB_HEADING_STYLE),
          buildSummaryStatsTable(s1Data.length, s2Data.length, totalCount, verifiedCount),
          spacer(),
          ...aiContent.executiveSummary.split('\n\n').map((para) => paragraph(para, ISSB_TEXT_STYLE)),
          heading('2. Completion Status by Pillar', ISSB_HEADING_STYLE),
          paragraph(
            'The table below summarises the completion status of all ISSB metrics, grouped by standard and pillar.',
            ISSB_TEXT_STYLE,
          ),
          buildPillarTable(pillarStats),
          heading('3. Full Metric Detail \u2014 IFRS S1', ISSB_HEADING_STYLE),
          paragraph(
            'The following tables list all IFRS S1 metrics with their current status and responses.',
            ISSB_TEXT_STYLE,
          ),
          ...s1Groups.flatMap(({ pillar, metrics }) => [
            paragraph(`IFRS S1 ${pillar} (${metrics.length})`, ISSB_BOLD_STYLE),
            buildMetricDetailTable(metrics, appendix, survey.unitConfig, displayUserInput),
          ]),
          heading('4. Full Metric Detail \u2014 IFRS S2', ISSB_HEADING_STYLE),
          paragraph(
            'The following tables list all IFRS S2 metrics with their current status and responses.',
            ISSB_TEXT_STYLE,
          ),
          ...s2Groups.flatMap(({ pillar, metrics }) => [
            paragraph(`IFRS S2 ${pillar} (${metrics.length})`, ISSB_BOLD_STYLE),
            buildMetricDetailTable(metrics, appendix, survey.unitConfig, displayUserInput),
          ]),
          heading('5. Recommended Next Steps', ISSB_HEADING_STYLE),
          paragraph(
            'Based on the current ISSB completion status, the following sequenced actions are recommended:',
            ISSB_TEXT_STYLE,
          ),
          ...buildNextStepsList(aiContent.nextSteps),
          paragraph(
            `This report was prepared using ${companyName}'s ${reportingPeriod} ISSB data. IFRS S1 and IFRS S2 are published by the International Sustainability Standards Board.`,
            ISSB_TEXT_STYLE,
          ),
        ],
      },
      ...(appendix.hasEntries()
        ? [
            {
              properties: {
                page: {
                  size: {
                    orientation: PageOrientation.LANDSCAPE,
                  },
                },
              },
              headers: { default: buildDefaultHeader(companyName) },
              footers: { default: buildDefaultFooter() },
              children: appendix.render(),
            },
          ]
        : []),
    ],
  });

  const blob = await Packer.toBlob(doc);
  const filename = `${generateFilename(survey, 'ISSB')}.docx`;
  saveAs(blob, filename);
};
